import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Headphones,
  HardDrive,
  KeyRound,
  LoaderCircle,
  Network,
  Phone,
  RefreshCw,
  Save,
  Server,
  Settings,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useAuth } from '../../auth';
import { userHasRole } from '../../roles';
import { telephonyApi } from '../telephonyApi';
import type { BrowserPhoneSettings, IceServer } from '../types';
import { TelephonyRecordingRetentionSettings } from './TelephonyRecordingRetentionSettings';

type Tab = 'PROVIDER' | 'WEBRTC' | 'RECORDINGS' | 'DIAGNOSTICS';

type GeneralForm = {
  enabled: boolean;
  mode: 'WEBRTC' | 'EXTERNAL';
  webSocketUrl: string;
  sipDomain: string;
  outboundProxy: string;
  registrationExpires: number;
  iceServersText: string;
  fallbackToExternal: boolean;
};

type BeelineForm = {
  enabled: boolean;
  sipUserId: string;
  authorizationUserId: string;
  sipPassword: string;
  domain: string;
  sipProxy: string;
  lineCount: number;
  transport: 'UDP' | 'TCP';
};

function iceServersToText(servers: IceServer[]): string {
  return JSON.stringify(servers || [], null, 2);
}

function parseIceServers(value: string): IceServer[] {
  let parsed: unknown;
  try { parsed = JSON.parse(value || '[]'); } catch { throw new Error('ICE/TURN указан невалидным JSON'); }
  if (!Array.isArray(parsed)) throw new Error('ICE/TURN должен быть JSON-массивом');
  for (const row of parsed) {
    if (!row || typeof row !== 'object' || !('urls' in row)) throw new Error('У каждого ICE-сервера должно быть поле urls');
  }
  return parsed as IceServer[];
}

export function TelephonySettings({ onClose, onOpenProviderIntegration }: { onClose?: () => void; onOpenProviderIntegration?: () => void }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('PROVIDER');
  const [general, setGeneral] = useState<GeneralForm | null>(null);
  const [beeline, setBeeline] = useState<BeelineForm | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const settingsQuery = useQuery({
    queryKey: ['telephony-browser-settings'],
    queryFn: telephonyApi.browserSettings,
    enabled: userHasRole(user, 'ADMIN'),
  });
  const beelineQuery = useQuery({
    queryKey: ['telephony-browser-beeline'],
    queryFn: telephonyApi.beelineSip,
    enabled: userHasRole(user, 'ADMIN'),
  });
  const pbxQuery = useQuery({
    queryKey: ['telephony-browser-pbx-status'],
    queryFn: telephonyApi.pbxStatus,
    enabled: userHasRole(user, 'ADMIN'),
    refetchInterval: tab === 'DIAGNOSTICS' ? 5_000 : false,
    retry: false,
  });

  useEffect(() => {
    const value = settingsQuery.data;
    if (!value) return;
    setGeneral({
      enabled: value.enabled,
      mode: value.mode,
      webSocketUrl: value.webSocketUrl,
      sipDomain: value.sipDomain,
      outboundProxy: value.outboundProxy,
      registrationExpires: value.registrationExpires,
      iceServersText: iceServersToText(value.iceServers),
      fallbackToExternal: value.fallbackToExternal,
    });
  }, [settingsQuery.data]);

  useEffect(() => {
    const value = beelineQuery.data;
    if (!value) return;
    setBeeline({
      enabled: value.enabled,
      sipUserId: value.sipUserId,
      authorizationUserId: value.authorizationUserId,
      sipPassword: '',
      domain: value.domain || 'ip.beeline.ru',
      sipProxy: value.sipProxy || 'ip.beeline.ru',
      lineCount: value.lineCount || 10,
      transport: value.transport || 'UDP',
    });
  }, [beelineQuery.data]);

  const saveGeneral = useMutation({
    mutationFn: async () => {
      if (!general) throw new Error('Настройки ещё не загружены');
      if (general.enabled && general.mode === 'WEBRTC' && !general.webSocketUrl.startsWith('wss://')) {
        throw new Error('Для WebRTC нужен защищённый адрес wss://');
      }
      const iceServers = parseIceServers(general.iceServersText);
      return telephonyApi.updateBrowserSettings({
        enabled: general.enabled,
        mode: general.mode,
        webSocketUrl: general.webSocketUrl.trim(),
        sipDomain: general.sipDomain.trim(),
        outboundProxy: general.outboundProxy.trim(),
        registrationExpires: Math.max(60, general.registrationExpires || 120),
        iceServers,
        fallbackToExternal: general.fallbackToExternal,
      });
    },
    onSuccess: async value => {
      setMessage('Настройки WebRTC сохранены');
      setError('');
      queryClient.setQueryData(['telephony-browser-settings'], value);
      await queryClient.invalidateQueries({ queryKey: ['telephony-browser-config'] });
      window.dispatchEvent(new Event('intez:phone-config-changed'));
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить настройки'),
  });

  const saveBeeline = useMutation({
    mutationFn: async () => {
      if (!beeline) throw new Error('Настройки Билайн ещё не загружены');
      if (beeline.enabled && (!beeline.sipUserId.trim() || !beeline.authorizationUserId.trim())) {
        throw new Error('Заполните SIP User ID и Authorization User ID');
      }
      if (beeline.enabled && !beelineQuery.data?.hasPassword && !beeline.sipPassword.trim()) {
        throw new Error('Введите SIP-пароль Билайн');
      }
      return telephonyApi.updateBeelineSip({
        enabled: beeline.enabled,
        domain: beeline.domain.trim() || 'ip.beeline.ru',
        sipProxy: beeline.sipProxy.trim() || 'ip.beeline.ru',
        sipUserId: beeline.sipUserId.trim(),
        authorizationUserId: beeline.authorizationUserId.trim(),
        ...(beeline.sipPassword.trim() ? { sipPassword: beeline.sipPassword.trim() } : {}),
        lineCount: Math.min(10, Math.max(1, Number(beeline.lineCount || 10))),
        transport: beeline.transport,
        notificationEmails: [],
      });
    },
    onSuccess: async () => {
      setMessage('Общий SIP-профиль Билайн сохранён');
      setError('');
      setBeeline(current => current ? { ...current, sipPassword: '' } : current);
      await Promise.all([
        beelineQuery.refetch(),
        pbxQuery.refetch(),
        queryClient.invalidateQueries({ queryKey: ['telephony-browser-config'] }),
      ]);
      window.dispatchEvent(new Event('intez:phone-config-changed'));
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить SIP Билайн'),
  });

  const validateBeeline = useMutation({
    mutationFn: telephonyApi.validateBeelineSip,
    onSuccess: async value => {
      if (value.ready) {
        setMessage(value.message || 'Билайн зарегистрирован');
        setError('');
      } else {
        setError(value.message || 'Регистрация Билайн не завершена');
      }
      await pbxQuery.refetch();
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось проверить Билайн'),
  });

  const provisionPbx = useMutation({
    mutationFn: telephonyApi.provisionPbx,
    onSuccess: async () => {
      setMessage('Конфигурация FreeSWITCH пересобрана');
      setError('');
      await Promise.all([pbxQuery.refetch(), queryClient.invalidateQueries({ queryKey: ['telephony-browser-config'] })]);
      window.dispatchEvent(new Event('intez:phone-config-changed'));
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось провизионировать FreeSWITCH'),
  });

  const loading = settingsQuery.isLoading || beelineQuery.isLoading;
  const pending = saveGeneral.isPending || saveBeeline.isPending || validateBeeline.isPending || provisionPbx.isPending;
  const pbx = pbxQuery.data;
  const readiness = useMemo(() => [
    { label: 'FreeSWITCH', ready: Boolean(pbx?.gatewayReachable) },
    { label: 'TURN/STUN', ready: Boolean(pbx?.turnReady) },
    { label: 'Билайн', ready: pbx?.registrationStatus === 'REGISTERED' },
    { label: 'WebRTC', ready: Number(pbx?.webRtcRegistered || 0) > 0 },
  ], [pbx]);

  if (!userHasRole(user, 'ADMIN')) return <div className="form-error">Системные настройки телефонии доступны только администратору.</div>;

  const content = <section className="telephony-settings-module telephony-settings-stage1">
    <header className="telephony-settings-head">
      <div><span><Settings /></span><div><small>СИСТЕМНЫЕ НАСТРОЙКИ</small><h2>Телефония</h2><p>Провайдер, WebRTC, записи и инфраструктура. Сотрудники настраиваются отдельно.</p></div></div>
      {onClose && <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть"><X /></button>}
    </header>

    <nav className="telephony-settings-tabs">
      <button type="button" className={tab === 'PROVIDER' ? 'active' : ''} onClick={() => setTab('PROVIDER')}><Phone /> Провайдер</button>
      <button type="button" className={tab === 'WEBRTC' ? 'active' : ''} onClick={() => setTab('WEBRTC')}><Headphones /> WebRTC</button>
      <button type="button" className={tab === 'RECORDINGS' ? 'active' : ''} onClick={() => setTab('RECORDINGS')}><HardDrive /> Записи</button>
      <button type="button" className={tab === 'DIAGNOSTICS' ? 'active' : ''} onClick={() => setTab('DIAGNOSTICS')}><Activity /> Диагностика</button>
    </nav>

    {loading && tab !== 'RECORDINGS' && <div className="telephony-settings-loading"><LoaderCircle /> Загружаем настройки…</div>}
    {error && <div className="form-error"><AlertTriangle /> {error}</div>}
    {message && <div className="form-success"><CheckCircle2 /> {message}</div>}

    {!loading && tab === 'PROVIDER' && beeline && <form className="telephony-settings-form" onSubmit={(event: FormEvent) => { event.preventDefault(); saveBeeline.mutate(); }}>
      <div className="telephony-provider-summary-stage1">
        <span><Phone /></span>
        <div><small>ОБЩИЙ ТРАНК</small><h3>Билайн</h3><p>Один системный SIP-профиль используется FreeSWITCH для всех сотрудников. В карточках сотрудников провайдерские учётные данные не хранятся.</p></div>
        <i className={pbx?.registrationStatus === 'REGISTERED' ? 'ok' : 'warn'}>{pbx?.registrationStatus === 'REGISTERED' ? 'REGED' : pbx?.registrationStatus || 'UNKNOWN'}</i>
      </div>
      <div className="telephony-settings-grid">
        <label className="switch-row"><input type="checkbox" checked={beeline.enabled} onChange={event => setBeeline(current => current ? { ...current, enabled: event.target.checked } : current)} /><span>Использовать общий SIP Билайн</span></label>
        <label>Транспорт<select value={beeline.transport} onChange={event => setBeeline(current => current ? { ...current, transport: event.target.value as 'UDP' | 'TCP' } : current)}><option value="UDP">UDP</option><option value="TCP">TCP</option></select></label>
        <label>SIP User ID<input value={beeline.sipUserId} onChange={event => setBeeline(current => current ? { ...current, sipUserId: event.target.value } : current)} /></label>
        <label>Authorization User ID<input value={beeline.authorizationUserId} onChange={event => setBeeline(current => current ? { ...current, authorizationUserId: event.target.value } : current)} /></label>
        <label>SIP-пароль<input type="password" value={beeline.sipPassword} onChange={event => setBeeline(current => current ? { ...current, sipPassword: event.target.value } : current)} placeholder={beelineQuery.data?.hasPassword ? 'Пароль сохранён; оставьте пустым без изменения' : 'Введите пароль'} /></label>
        <label>Лимит линий<input type="number" min={1} max={10} value={beeline.lineCount} onChange={event => setBeeline(current => current ? { ...current, lineCount: Number(event.target.value) } : current)} /></label>
        <label>Домен<input value={beeline.domain} onChange={event => setBeeline(current => current ? { ...current, domain: event.target.value } : current)} /></label>
        <label>SIP proxy<input value={beeline.sipProxy} onChange={event => setBeeline(current => current ? { ...current, sipProxy: event.target.value } : current)} /></label>
      </div>
      {beelineQuery.data?.warnings?.length ? <div className="telephony-settings-warnings">{beelineQuery.data.warnings.map(item => <span key={item}><AlertTriangle /> {item}</span>)}</div> : null}
      <div className="form-actions telephony-provider-actions-stage1">
        {onOpenProviderIntegration && <button type="button" className="button ghost" disabled={pending} onClick={onOpenProviderIntegration}><Network /> API и подписки Билайн</button>}
        <span />
        <button type="button" className="button ghost" disabled={pending} onClick={() => validateBeeline.mutate()}><ShieldCheck /> Проверить регистрацию</button>
        <button className="button primary" disabled={pending}><Save /> Сохранить</button>
      </div>
    </form>}

    {!loading && tab === 'WEBRTC' && general && <form className="telephony-settings-form" onSubmit={(event: FormEvent) => { event.preventDefault(); saveGeneral.mutate(); }}>
      <div className="telephony-settings-section-note"><Headphones /><span><b>Браузерный телефон сотрудников</b><small>Эти параметры общие для портала. Добавочные и права сотрудников задаются в разделе «Сотрудники».</small></span></div>
      <div className="telephony-settings-grid">
        <label className="switch-row"><input type="checkbox" checked={general.enabled} onChange={event => setGeneral(current => current ? { ...current, enabled: event.target.checked } : current)} /><span>Включить браузерный телефон</span></label>
        <label>Режим<select value={general.mode} onChange={event => setGeneral(current => current ? { ...current, mode: event.target.value as GeneralForm['mode'] } : current)}><option value="WEBRTC">WebRTC в портале</option><option value="EXTERNAL">Внешнее устройство</option></select></label>
        <label>WSS URL<input value={general.webSocketUrl} onChange={event => setGeneral(current => current ? { ...current, webSocketUrl: event.target.value } : current)} placeholder="wss://helpdesk.intezgroup.ru/telephony/ws" /></label>
        <label>SIP-домен<input value={general.sipDomain} onChange={event => setGeneral(current => current ? { ...current, sipDomain: event.target.value } : current)} placeholder="helpdesk.intezgroup.ru" /></label>
        <label>Outbound proxy<input value={general.outboundProxy} onChange={event => setGeneral(current => current ? { ...current, outboundProxy: event.target.value } : current)} placeholder="оставьте пустым для FreeSWITCH" /></label>
        <label>Регистрация, секунд<input type="number" min={60} max={3600} value={general.registrationExpires} onChange={event => setGeneral(current => current ? { ...current, registrationExpires: Number(event.target.value) } : current)} /></label>
      </div>
      <label>ICE/TURN, JSON<textarea rows={8} spellCheck={false} value={general.iceServersText} onChange={event => setGeneral(current => current ? { ...current, iceServersText: event.target.value } : current)} /></label>
      <label className="switch-row"><input type="checkbox" checked={general.fallbackToExternal} onChange={event => setGeneral(current => current ? { ...current, fallbackToExternal: event.target.checked } : current)} /><span>Разрешить резервный звонок через внешнее устройство</span></label>
      <div className="form-actions"><button className="button primary" disabled={pending}><Save /> Сохранить WebRTC</button></div>
    </form>}

    {tab === 'RECORDINGS' && <TelephonyRecordingRetentionSettings />}

    {!loading && tab === 'DIAGNOSTICS' && <div className="telephony-settings-diagnostics">
      <div className="telephony-readiness-grid">{readiness.map(item => <article key={item.label} className={item.ready ? 'ready' : 'failed'}>{item.ready ? <CheckCircle2 /> : <AlertTriangle />}<span><b>{item.label}</b><small>{item.ready ? 'Готово' : 'Требует проверки'}</small></span></article>)}</div>
      <dl>
        <div><dt>Регистрация Билайн</dt><dd>{pbx?.registrationStatus || 'UNKNOWN'}</dd></div>
        <div><dt>WebRTC-операторов</dt><dd>{String(pbx?.webRtcRegistered ?? 0)}</dd></div>
        <div><dt>Активных каналов</dt><dd>{String(pbx?.activeChannels ?? 0)}</dd></div>
        <div><dt>WSS</dt><dd>{pbx?.webSocketUrl || general?.webSocketUrl || '—'}</dd></div>
        <div><dt>SIP-домен</dt><dd>{pbx?.sipDomain || general?.sipDomain || '—'}</dd></div>
        <div><dt>RTP</dt><dd>{String(pbx?.rtpRange || '—')}</dd></div>
        <div><dt>TURN relay</dt><dd>{String(pbx?.turnRange || '—')}</dd></div>
      </dl>
      <div className="form-actions">
        <button type="button" className="button ghost" disabled={pbxQuery.isFetching || pending} onClick={() => void pbxQuery.refetch()}><RefreshCw /> Обновить</button>
        <button type="button" className="button primary" disabled={pending} onClick={() => provisionPbx.mutate()}><Server /> Пересобрать FreeSWITCH</button>
      </div>
    </div>}
  </section>;

  return onClose ? <div className="modal-backdrop telephony-settings-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}><div className="telephony-settings-modal">{content}</div></div> : content;
}
